import { toast } from "sonner";
import { PANELS, layoutPanels, bioValues } from "./FreezeHud";
import { BONES, RIG_LABELS } from "./pose";

const GOLD = "#F5C518", CRIMSON = "#C41E24", MONO = '"Roboto Mono", monospace';
const W = 1080, H = 1350, TOP = 150, VIDEO_H = 1000;

const rr = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
};

const glow = (ctx, color, blur, fn) => { ctx.save(); ctx.shadowColor = color; ctx.shadowBlur = blur; fn(); ctx.restore(); };

const drawFrame = (ctx, video) => {
  ctx.fillStyle = "#050505"; ctx.fillRect(0, TOP, W, VIDEO_H);
  const vw = video.videoWidth || 16, vh = video.videoHeight || 9;
  const s = Math.min(W / vw, VIDEO_H / vh);
  const dw = vw * s, dh = vh * s, ox = (W - dw) / 2, oy = TOP + (VIDEO_H - dh) / 2;
  try { ctx.drawImage(video, ox, oy, dw, dh); } catch (e) { /* frame unavailable — keep dark slate */ }
  ctx.fillStyle = "rgba(0,0,0,0.42)"; ctx.fillRect(ox, oy, dw, dh);
  return { ox, oy, dw, dh };
};

const drawReticle = (ctx, x, y) => {
  glow(ctx, GOLD, 26, () => {
    ctx.strokeStyle = GOLD; ctx.lineWidth = 4;
    ctx.beginPath(); ctx.arc(x, y, 54, 0, Math.PI * 2); ctx.stroke();
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(x, y - 76); ctx.lineTo(x, y + 76); ctx.moveTo(x - 76, y); ctx.lineTo(x + 76, y); ctx.stroke();
    ctx.fillStyle = GOLD; ctx.beginPath(); ctx.arc(x, y, 7, 0, Math.PI * 2); ctx.fill();
  });
  ctx.strokeStyle = "rgba(0,229,255,0.45)"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(x, y, 92, 0, Math.PI * 2); ctx.stroke();
};

const drawPanel = (ctx, x, y, label, value) => {
  const w = 232, h = 78;
  glow(ctx, GOLD, 18, () => { ctx.fillStyle = "rgba(0,0,0,0.85)"; rr(ctx, x - w / 2, y - h / 2, w, h, 6); ctx.fill(); });
  ctx.strokeStyle = "rgba(245,197,24,0.7)"; ctx.lineWidth = 2; rr(ctx, x - w / 2, y - h / 2, w, h, 6); ctx.stroke();
  ctx.fillStyle = GOLD; ctx.fillRect(x - w / 2, y - h / 2 + 6, 4, h - 12);
  ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "rgba(245,197,24,0.75)"; ctx.font = `500 15px ${MONO}`; ctx.fillText(label, x - w / 2 + 18, y - 12);
  ctx.fillStyle = GOLD; ctx.font = `700 32px ${MONO}`; ctx.fillText(value, x - w / 2 + 18, y + 24);
};

const drawSkeleton = (ctx, rect, skel) => {
  const P = (k) => skel[k] && { x: rect.ox + skel[k].x * rect.dw, y: rect.oy + skel[k].y * rect.dh };
  glow(ctx, GOLD, 14, () => {
    ctx.strokeStyle = GOLD; ctx.lineWidth = 5; ctx.lineCap = "round";
    BONES.forEach(([a, b]) => { const p = P(a), q = P(b); if (!p || !q) return; ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y); ctx.stroke(); });
  });
  Object.keys(RIG_LABELS).forEach((k) => {
    const p = P(k); if (!p) return;
    const r = k === "head" ? 16 : 9;
    glow(ctx, GOLD, 12, () => { ctx.fillStyle = "#0a0a0a"; ctx.strokeStyle = GOLD; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); });
  });
};

const drawHud = (ctx, rect, nx, ny, engine, joint, skel) => {
  const cx = rect.ox + nx * rect.dw, cy = rect.oy + ny * rect.dh;
  const pct = { x: ((cx) / W) * 100, y: ((cy - TOP) / VIDEO_H) * 100 };
  const v = bioValues(engine);
  const spots = layoutPanels(pct.x, pct.y).map((s) => ({ x: (s.x / 100) * W, y: TOP + (s.y / 100) * VIDEO_H }));
  if (skel) drawSkeleton(ctx, rect, skel);
  glow(ctx, GOLD, 12, () => {
    ctx.strokeStyle = GOLD; ctx.lineWidth = 2.5; ctx.lineJoin = "round";
    spots.forEach((s) => { const ex = cx + (s.x - cx) * 0.58; ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(ex, s.y); ctx.lineTo(s.x, s.y); ctx.stroke(); });
  });
  PANELS.forEach((p, i) => drawPanel(ctx, spots[i].x, spots[i].y, p.label, p.fmt(v)));
  if (skel) {
    glow(ctx, CRIMSON, 18, () => { ctx.strokeStyle = CRIMSON; ctx.lineWidth = 4; ctx.beginPath(); ctx.arc(cx, cy, 30, 0, Math.PI * 2); ctx.stroke(); });
  } else drawReticle(ctx, cx, cy);
  const tag = `◎ LOCK${joint ? ` · ${joint.toUpperCase()}` : ""}`;
  ctx.font = `600 18px ${MONO}`; ctx.textAlign = "left";
  const tw = ctx.measureText(tag).width + 20, ty = ny < 0.18 ? cy + 66 : cy - 96;
  ctx.fillStyle = "rgba(0,0,0,0.7)"; rr(ctx, cx + 62, ty, tw, 30, 4); ctx.fill();
  ctx.fillStyle = GOLD; ctx.fillText(tag, cx + 72, ty + 21);
};

const drawChrome = (ctx, { playerName, drill, joint, t, cue }) => {
  ctx.fillStyle = "#101010"; ctx.fillRect(0, 0, W, TOP); ctx.fillRect(0, TOP + VIDEO_H, W, H - TOP - VIDEO_H);
  const brush = ctx.createLinearGradient(0, 0, 0, TOP); brush.addColorStop(0, "rgba(255,255,255,0.08)"); brush.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = brush; ctx.fillRect(0, 0, W, TOP);
  ctx.fillStyle = "rgba(245,197,24,0.35)"; ctx.fillRect(0, TOP - 2, W, 2); ctx.fillRect(0, TOP + VIDEO_H, W, 2);
  glow(ctx, GOLD, 16, () => { ctx.fillStyle = GOLD; ctx.beginPath(); ctx.arc(52, 58, 8, 0, Math.PI * 2); ctx.fill(); });
  ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  ctx.fillStyle = GOLD; ctx.font = `600 20px ${MONO}`; ctx.fillText("SYSTEM_ACTIVE  ·  NEXT O ANALYSIS STATION", 76, 66);
  ctx.fillStyle = "#fff"; ctx.font = "700 54px Anton, Impact, sans-serif"; ctx.fillText("NEXT O SOCCER ENGINE", 48, 124);
  ctx.textAlign = "right"; ctx.fillStyle = "rgba(255,255,255,0.55)"; ctx.font = `500 18px ${MONO}`;
  ctx.fillText(`FREEZE_FRAME${typeof t === "number" ? ` · T+${t.toFixed(2)}s` : ""}`, W - 48, 66);
  ctx.fillStyle = CRIMSON; ctx.font = `600 20px ${MONO}`; ctx.fillText("TECHNICAL PERFORMANCE ENGINE", W - 48, 122);
  const fy = TOP + VIDEO_H;
  ctx.textAlign = "left";
  ctx.fillStyle = GOLD; ctx.font = `600 18px ${MONO}`; ctx.fillText((joint ? `LOCK · ${joint}` : "BIOMECHANICAL FREEZE").toUpperCase(), 48, fy + 52);
  ctx.fillStyle = "#fff"; ctx.font = "700 46px Anton, Impact, sans-serif";
  ctx.fillText(`${(playerName || "NEXT O PLAYER").toUpperCase()}${drill ? `  ·  ${drill.toUpperCase()}` : ""}`.slice(0, 44), 48, fy + 108);
  if (cue) { ctx.fillStyle = "rgba(255,255,255,0.7)"; ctx.font = `500 22px ${MONO}`; ctx.fillText(cue.slice(0, 70), 48, fy + 152); }
  ctx.textAlign = "right"; ctx.fillStyle = CRIMSON; ctx.font = "700 26px Arial"; ctx.fillText("nextosoccer.com", W - 48, fy + 152);
};

export async function exportFreezeFrame({ video, engine, playerName, drill, skeleton }) {
  const ef = engine?.error_frame;
  const nx = ef ? (ef.cx != null ? ef.cx : ef.x + ef.w / 2) : 0.5;
  const ny = ef ? (ef.cy != null ? ef.cy : ef.y + ef.h / 2) : 0.5;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const rect = drawFrame(ctx, video);
  drawHud(ctx, rect, nx, ny, engine, ef?.joint, skeleton);
  drawChrome(ctx, { playerName, drill, joint: ef?.joint, t: ef?.t, cue: ef?.cue });

  const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
  if (!blob) throw new Error("export failed");
  const file = new File([blob], "next-o-freeze-frame.png", { type: "image/png" });
  const caption = `My Next O Soccer Engine freeze-frame${ef?.joint ? ` · ${ef.joint}` : ""} ⚽ #NextOSoccer`;
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text: caption, title: "Next O Soccer Freeze Frame" });
      return "shared";
    }
  } catch (e) { /* cancelled or unsupported → download */ }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "next-o-freeze-frame.png";
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  toast.success("Freeze frame saved — share it anywhere or send it to your coach!");
  return "downloaded";
}
